import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import Select from "react-select";
import "../../css/Create.css";
import "../../css/Account.css"
import "../../css/ImportDocument.css";
import "../../css/DocumentationHomeComponent.css"
import "../../css/FinalizeImportedDocument.css"
import { notifyBlockedShareRecipients, readShareResponse } from "../../utils/sharePermissions";

function FinalizeImportedDocument({ onBack, documentTitle, selectedFile, imageAttachments = [], content, enhancedRightClickEnabled, showContextMenu, ContextMenuComponent }) {
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Loading...");
    
    // Document type and status states
    const [selectedType, setSelectedType] = useState("");
    const [isPublic, setIsPublic] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [hasSpaces, setHasSpaces] = useState(false);

    // Content and metadata states
    const [name, setName] = useState(documentTitle || "");
    const [description, setDescription] = useState("");

    // Document relationship states
    const [allDocumentation, setAllDocumentation] = useState([]);
    const [selectedSpace, setSelectedSpace] = useState("");
    const [selectedParent, setSelectedParent] = useState("");

    // User and authentication states
    const [uid, setUid] = useState("");
    const [userEmail, setUserEmail] = useState("");

    // Collaboration states
    const [emails, setEmails] = useState([]);
    const [currentEmail, setCurrentEmail] = useState("");

    // Social media and custom link states
    const [customLink, setCustomLink] = useState("");
    const [customLinkTitle, setCustomLinkTitle] = useState("");
    const [discordLink, setDiscordLink] = useState("");
    const [linkedInLink, setLinkedInLink] = useState("");
    const [youtubeLink, setYoutubeLink] = useState("");
    const [githubLink, setGithubLink] = useState("");
    const [twitterLink, setTwitterLink] = useState("");

    const navigate = useNavigate();

    // Authentication Effect
    useEffect(() => {
        setLoadingMessage('Checking authentication...');
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
                setUserEmail(user.email);
            } else {
                navigate("/login");
            }
        });

        return () => {
            unsubscribe();
        };
    }, [navigate]);

    // User Data Fetching Effect
    useEffect(() => {
        if (uid) {
            fetchPageDetails();
            fetchAccountDetails();
        }
    }, [uid]);

    // Documentation Population Effect
    useEffect(() => {
        populateDocumentation();
    }, [uid]);

    // Theme Configuration Effect

    const fetchAccountDetails = async () => {
        setLoadingMessage('Fetching account details...');
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;
            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/users/${uid}/`,
                token ? {
                    headers: {
                        'auth-token': `Bearer ${token}`,
                        'Authorization': `${token}`
                    }
                } : undefined
            );
            const data = await response.json();

            if (response.ok) {
                setYoutubeLink(data.youtube_channel || "");
                setCustomLink(data.custom_domain || "");
                setCustomLinkTitle(data.custom_domain_title || "");
                setDiscordLink(data.discord_server || "");
                setLinkedInLink(data.linkedin_link || "");
                setGithubLink(data.github_link || "");
                setTwitterLink(data.twitter_link || "");
            }
        } catch (error) {
            console.error("Error in fetching account details: ", error);
        }
    };

    const populateDocumentation = async () => {
        setLoadingMessage('Populating documentation...');
        const token = await auth.currentUser.getIdToken();
        try {
            const [spaceResponse, pageResponse] = await Promise.all([
                fetch(
                    "https://bluebirddocumentationadmin.pythonanywhere.com/spaces/author/" +
                        uid +
                        "/",
                    {
                        headers: {
                            'Authorization': `${token}`
                        }
                    }
                ),
                fetch(
                    "https://bluebirddocumentationadmin.pythonanywhere.com/pages/author/" +
                        uid +
                        "/",
                    {
                        headers: {
                            'Authorization': `${token}`
                        }
                    }
                ),
            ]);

            const spaceData =
                spaceResponse.status === 200
                    ? await spaceResponse.json()
                    : { spaces: [] };
            const pageData =
                pageResponse.status === 200 || pageResponse.status === 404
                    ? await pageResponse.json()
                    : [];

            const spaceOptions = Array.isArray(spaceData.spaces)
                ? spaceData.spaces.map((space) => ({
                      label: space.name,
                      value: space.id,
                  }))
                : [];
            const pageOptions = Array.isArray(pageData.pages)
                ? pageData.pages.map((page) => ({
                      label: page.page_name,
                      value: page.id,
                  }))
                : [];

            const combinedOptions = [...spaceOptions, ...pageOptions];
            setAllDocumentation(combinedOptions);

            if (spaceOptions.length > 0) {
                setHasSpaces(true);
            }
        } catch (error) {
            console.error("Error in populating documentation: ", error);
            setAllDocumentation([]);
        }
    };

    const fetchPageDetails = async () => {
        setLoadingMessage('Fetching page details...');
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/pages/author/${uid}/`,
                {
                    headers: {
                        'Authorization': `${token}`
                    }
                }
            );
            if (response.status === 200) {
                const data = await response.json();
                if (data.pages && data.pages.length > 0) {
                    setHasSpaces(true);
                }
            }
        } catch (error) {
            console.error("Error in fetching page details: ", error);
        }
    };

    const maxLengths = {
        Space: 100,
        Page: 150,
    };

    const currentMaxLength = maxLengths[selectedType] || 100;

    const handleToggle = (e) => {
        setIsPublic(e.target.checked);
    };

    const handleCompletedToggle = (e) => {
        setCompleted(e.target.checked);
    };

    const handleSelectedType = (e) => {
        setSelectedType(e.target.value);
    };

    const handleAddEmail = () => {
        if (currentEmail && !emails.some((item) => item.email === currentEmail)) {
            if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentEmail)) {
                setEmails([...emails, { email: currentEmail, permission: "Can Edit" }]);
                setCurrentEmail("");
            } else {
                alert("Please enter a valid email address.");
            }
        } else {
            alert("Email is already added or invalid.");
        }
    };

    const handleRemoveEmail = (index) => {
        const updatedEmails = [...emails];
        updatedEmails.splice(index, 1);
        setEmails(updatedEmails);
    };

    const handlePermissionChange = (index, newPermission) => {
        const updatedEmails = [...emails];
        updatedEmails[index].permission = newPermission;
        setEmails(updatedEmails);
    };

    const createPermissionTableEntry = async () => {
        const data = {
            name: name,
            documentationType: selectedType,
            userEmail: emails,
            isPublic: isPublic,
            documentUid: uid,
            documentUrl: `documentation/${selectedType.toLowerCase()}/${name}/${uid}/`,
            description: description,
        };

        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;
            if (!token) {
                throw new Error("User is not authenticated.");
            }
            const response = await fetch(
                "https://bluebirddocumentationadmin.pythonanywhere.com/permissions/create/",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                    body: JSON.stringify(data),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || "Failed to create permissions.");
            }

            // BBD-73: a 201 can still be partial — addresses on either side of a
            // block are skipped and reported back in blocked_emails.
            notifyBlockedShareRecipients(await readShareResponse(response));

            console.log("Successfully set permissions.");
        } catch (error) {
            console.error("Error creating permission table entry:", error);
            throw error;
        }
    };

    const handleSubmit = async () => {
        // Validation checks
        if (!selectedType) {
            alert("Please select a content type (Space or Page).");
            return;
        }

        if (!name || name.trim() === "") {
            alert("Please enter a name for your document.");
            return;
        }

        // Check that name only contains letters, numbers, spaces, periods, dashes, and underscores
        if (!/^[a-zA-Z0-9\s\.\-\_]+$/.test(name)) {
            alert(
                "Name must contain only letters, numbers, spaces, periods, dashes, and underscores."
            );
            return;
        }

        if (selectedType === "Page" && !selectedParent) {
            alert("Please select a parent space or page for your new page.");
            return;
        }

        if (!content || content.trim() === "") {
            alert("Content cannot be empty. Please go back and ensure your document was imported correctly.");
            return;
        }

        // Set loading to true immediately after validation passes
        setIsLoading(true);
        setLoadingMessage("Creating document...");

        try {
            if (emails.length > 0) {
                await createPermissionTableEntry();
            }

            // Prepare the document data using the same structure as Create.js
            const data = {
                type: selectedType,
                isPublic: isPublic,
                emails: emails,
                content: content,
                name: name,
                author: uid,
                completed: completed,
                description: description,
                space: selectedSpace,
                parentPage: selectedParent,
                customLink: customLink,
                customLinkTitle: customLinkTitle,
                twitterLink: twitterLink,
                discordLink: discordLink,
                linkedInLink: linkedInLink,
                githubLink: githubLink,
                youtubeLink: youtubeLink,
            };

            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                throw new Error("User is not authenticated.");
            }

            // Use the same unified endpoint as Create.js
            const response = await fetch(
                "https://bluebirddocumentationadmin.pythonanywhere.com/documentation/create/",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                    },
                    body: JSON.stringify(data),
                }
            );

            if (response.ok) {
                console.log(`${selectedType} created successfully!`);
                navigate(`/documentation/${selectedType.toLowerCase()}/${name}/${uid}/`);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to create ${selectedType.toLowerCase()}`);
            }

        } catch (error) {
            console.error(`Error creating ${selectedType.toLowerCase()}:`, error);
            alert(`Error creating ${selectedType.toLowerCase()}: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackClick = () => {
        if (onBack) {
            setIsTransitioning(true);
            setTimeout(() => {
                onBack();
            }, 150);
        }
    };

    if (!uid) {
        return (
            <div className="DocumentationMainPage__div">
                <div className="DocumentationMainPage__main-content">
                    <p className="large">It looks like you're not signed in.</p>
                    <p className="medium">
                        <strong>
                            To create a document, you must have an account with us
                        </strong>
                        . Please log in to continue.
                    </p>
                    <button
                        onClick={() => navigate("/login")}
                        className="ConflictResolver__button"
                    >
                        Login
                    </button>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
                <div className="DocumentationHomeComponent__coffee-loader-div">
                        <div className="coffee-cup-loader">
                                <div className="cup">
                                        <div className="cup-body"></div>
                                        <div className="cup-handle"></div>
                                        <div className="cup-base"></div>
                                        <div className="cup-shadow"></div>
                                        <div className="steam steam1"></div>
                                        <div className="steam steam2"></div>
                                        <div className="steam steam3"></div>
                                </div>
                        </div>
                        <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
                </div>
        )
    }

    return (
        <div 
            className={`ImportDocument__main-div ${isTransitioning ? 'transitioning-back' : ''}`}
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >
            <div className="ImportDocument__content">
                <h2>Almost Done!</h2>
                
                <p className="medium" style={{ marginTop: "20px" }}>
                    Configure the settings for your imported document.
                </p>

                {/* Document Type Selection */}
                <div className="Create__form-group">
                    <select
                        className="form-select Create__input"
                        aria-label="Default select example"
                        name="selected_type"
                        value={selectedType}
                        onChange={handleSelectedType}
                        required
                    >
                        <option value="">- Select Content Type -</option>
                        <option value="Space">Space</option>
                        <option value="Page" disabled={!hasSpaces}>
                            Page
                        </option>
                    </select>
                </div>

                {/* Parent Selection for Pages */}
                {selectedType === "Page" && (
                    <div className="Create__form-group">
                        <label style={{ paddingTop: "3px" }}>
                            Select a parent space or page:{" "}
                        </label>
                        <Select
                            options={allDocumentation}
                            value={allDocumentation.find(
                                (option) => option.value === selectedParent
                            )}
                            onChange={(option) =>
                                setSelectedParent(option ? option.label : "")
                            }
                            placeholder="Select a Space"
                            isSearchable={true}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                )}

                {/* Document Name */}
                <div className="Create__form-group">
                    <label>Name: </label>
                    <input
                        type="text"
                        id="name"
                        className="Create__input-short-field"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={currentMaxLength}
                        placeholder={`Max ${currentMaxLength} characters`}
                        required
                    />
                </div>

                {/* Description */}
                <div className="Create__form-group">
                    <label>Short Description: </label>
                    <input
                        type="text"
                        id="description"
                        className="Create__input-full-field"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Enter a brief description of this document"
                    />
                </div>
                <div className="Create__form-group-helper-text">
                    <i
                        className="fas fa-info-circle Create__info-icon"
                        title="Information"
                    ></i>
                    <p className="Create__helper-text">
                        This description is what invited users will see about this
                        space/page.
                    </p>
                </div>

                {/* Public Toggle */}
                <div className="Create__form-group">
                    <label>Make this content public?</label>
                    <label className="Create__switch">
                        <input type="checkbox" checked={isPublic} onChange={handleToggle} />
                        <span className="Create__slider round"></span>
                    </label>
                </div>
                <div className="Create__form-group-helper-text">
                    <i
                        className="fas fa-info-circle Create__info-icon"
                        title="Information"
                    ></i>
                    <p className="Create__helper-text">
                        Public content is viewable by anyone that has this document's URL.
                    </p>
                </div>

                {/* Publish Toggle */}
                <div className="Create__form-group">
                    <label>Publish this page upon submitting?</label>
                    <label className="Create__switch">
                        <input
                            type="checkbox"
                            checked={completed}
                            onChange={handleCompletedToggle}
                        />
                        <span className="Create__slider round"></span>
                    </label>
                </div>
                <div className="Create__form-group-helper-text">
                    <i
                        className="fas fa-info-circle Create__info-icon"
                        title="Information"
                    ></i>
                    <p className="Create__helper-text">
                        Whether the document is public or not, this document will not be{" "}
                        <strong>viewable</strong> by anyone; including added collaborators
                        that have "Can View" permission. Collaborators that have "Can Edit"
                        permission can edit this document.
                    </p>
                </div>

                {/* Collaborators Section */}
                <div
                    className="Create__form-group"
                    style={{ flexDirection: "column", marginBottom: "60px" }}
                >
                    <label>Invite others to collaborate on this content.</label>
                    {emails.map((item, index) => (
                        <div
                            key={index}
                            className="Create__email-item"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "10px",
                            }}
                        >
                            <span>{item.email}</span>
                            <select
                                value={item.permission}
                                className="Create__select"
                                onChange={(e) => handlePermissionChange(index, e.target.value)}
                                style={{ marginLeft: "10px", padding: "5px" }}
                            >
                                <option value="Can Edit">Can Edit</option>
                                <option value="Can View">Can View</option>
                            </select>
                            <button
                                onClick={() => handleRemoveEmail(index)}
                                style={{
                                    marginLeft: "10px",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                }}
                            >
                                <i
                                    className="fas fa-trash-alt"
                                    title="Remove Email"
                                    style={{ color: "white" }}
                                ></i>
                            </button>
                        </div>
                    ))}
                    <div className="Create__email-input-container">
                        <input
                            type="email"
                            className="Create__email-input"
                            placeholder="Enter E-mail Address"
                            value={currentEmail}
                            onChange={(e) => setCurrentEmail(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleAddEmail()}
                        />
                        <button onClick={handleAddEmail} className="Create__add-email-btn">
                            <i className="fas fa-plus"></i>
                        </button>
                    </div>
                    <div className="Create__form-group-helper-text">
                        <i
                            className="fas fa-info-circle Create__info-icon"
                            title="Information"
                        ></i>
                        <p className="Create__helper-text">
                            As of this current version, any added permissions will{" "}
                            <strong>not</strong> automatically be added to any future child
                            documents that are created.
                        </p>
                    </div>
                </div>

                {/* Social Links Section */}
                <div style={{ marginBottom: "60px" }}>
                    <h3>Social Links (Optional)</h3>
                    <p className="Create__helper-text" style={{ marginBottom: "20px" }}>
                        These links will be displayed to users viewing this document.
                    </p>

                    <input
                        type="text"
                        placeholder="Custom Link Title"
                        value={customLinkTitle}
                        onChange={(e) => setCustomLinkTitle(e.target.value)}
                        className="FinalizeImportedDocument__input-custom-title"
                        style={{ marginBottom: "10px" }}
                    />
                    <input
                        type="text"
                        placeholder="Custom Link Domain"
                        value={customLink}
                        onChange={(e) => setCustomLink(e.target.value)}
                        className="FinalizeImportedDocument__edit-input"
                        style={{ marginBottom: "15px" }}
                    />

                    <div className="Account__link-divs">
                        <i className="fa-brands fa-discord Account__icons"></i>
                        <input
                            type="text"
                            value={discordLink}
                            placeholder="Discord Server Link"
                            onChange={(e) => setDiscordLink(e.target.value)}
                            className="FinalizeImportedDocument__edit-input"
                        />
                    </div>

                    <div className="Account__link-divs">
                        <i className="fa-brands fa-square-x-twitter Account__icons"></i>
                        <input
                            type="text"
                            value={twitterLink}
                            placeholder="X Profile Link"
                            onChange={(e) => setTwitterLink(e.target.value)}
                            className="FinalizeImportedDocument__edit-input"
                        />
                    </div>

                    <div className="Account__link-divs">
                        <i className="fa-brands fa-linkedin Account__icons"></i>
                        <input
                            type="text"
                            value={linkedInLink}
                            placeholder="LinkedIn Profile Link"
                            onChange={(e) => setLinkedInLink(e.target.value)}
                            className="FinalizeImportedDocument__edit-input"
                        />
                    </div>

                    <div className="Account__link-divs">
                        <i className="fa-brands fa-youtube Account__icons"></i>
                        <input
                            type="text"
                            value={youtubeLink}
                            placeholder="YouTube Channel Link"
                            onChange={(e) => setYoutubeLink(e.target.value)}
                            className="FinalizeImportedDocument__edit-input"
                        />
                    </div>

                    <div className="Account__link-divs">
                        <i className="fa-brands fa-square-github Account__icons"></i>
                        <input
                            type="text"
                            value={githubLink}
                            placeholder="GitHub Profile Link"
                            onChange={(e) => setGithubLink(e.target.value)}
                            className="FinalizeImportedDocument__edit-input"
                        />
                    </div>
                </div>

                {/* Submit Button */}
                <button onClick={handleSubmit} className="submit-button" style={{ marginBottom: "60px" }}>
                    Create Imported Document
                </button>
            </div>

            {/* Back Arrow */}
            <div 
                className="ImportDocument__arrow ImportDocument__arrow--back" 
                onClick={handleBackClick}
                title="Go back to edit content"
                aria-label="Go back to edit content"
            >
                ❮
            </div>
            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default FinalizeImportedDocument;
